// Browser-safe consent contract shared by the head bootstrap, the consent UI and tests.
// No network or DOM access here.

export const CONSENT_STORAGE_KEY = "seoevo_consent";
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 180;
export const CONSENT_MAX_AGE_MS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
export const GTM_ID = "GTM-M74C68S5";
export const GA4_MEASUREMENT_ID = "G-8M2Q7R08YX";
export const GA4_DISABLE_KEY = `ga-disable-${GA4_MEASUREMENT_ID}` as const;
export const PRODUCTION_HOSTS = ["agenciaseoevolution.com", "www.agenciaseoevolution.com"] as const;
export const CONSENT_UPDATE_EVENT = "seoevo_consent_update";
export const OPEN_CONSENT_EVENT = "seoevo:open-consent";

export type ConsentChoice = {
  v: number;
  analytics: boolean;
  ads: boolean;
  /** epoch ms of the decision */
  ts: number;
};

export type GoogleConsentState = {
  ad_storage: "granted" | "denied";
  ad_user_data: "granted" | "denied";
  ad_personalization: "granted" | "denied";
  analytics_storage: "granted" | "denied";
};

export const CONSENT_DEFAULTS: GoogleConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

export function makeChoice(analytics: boolean, ads: boolean, now: number): ConsentChoice {
  return { v: CONSENT_VERSION, analytics: analytics === true, ads: ads === true, ts: now };
}

/** Returns a valid, unexpired choice of the current version, otherwise null (banner shows again). */
export function parseStoredConsent(raw: string | null | undefined, now: number): ConsentChoice | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw);
    if (!d || d.v !== CONSENT_VERSION) return null;
    if (typeof d.analytics !== "boolean" || typeof d.ads !== "boolean") return null;
    if (typeof d.ts !== "number" || !Number.isFinite(d.ts) || d.ts > now + 60_000) return null;
    if (now - d.ts > CONSENT_MAX_AGE_MS) return null;
    return { v: d.v, analytics: d.analytics, ads: d.ads, ts: d.ts };
  } catch {
    return null;
  }
}

export function toGoogleConsent(choice: ConsentChoice | null): GoogleConsentState {
  if (!choice) return { ...CONSENT_DEFAULTS };
  const ads = choice.ads ? "granted" : "denied";
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: choice.analytics ? "granted" : "denied",
  };
}

export function isProductionHost(host: string | null | undefined): boolean {
  return !!host && (PRODUCTION_HOSTS as readonly string[]).includes(host.toLowerCase());
}

/** GTM only loads on the real domain and only when at least one optional category is granted. */
export function shouldLoadGtm(choice: ConsentChoice | null, host: string): boolean {
  return isProductionHost(host) && !!choice && (choice.analytics || choice.ads);
}

export function isRevocation(prev: ConsentChoice | null, next: ConsentChoice): boolean {
  if (!prev) return false;
  return (prev.analytics && !next.analytics) || (prev.ads && !next.ads);
}

export function optionalChanged(prev: ConsentChoice | null, next: ConsentChoice): boolean {
  return (prev?.analytics ?? false) !== next.analytics || (prev?.ads ?? false) !== next.ads;
}

/**
 * With GTM already running, any optional change (grant or revocation) reloads so the stored choice is
 * replayed as default→update before any tag. Only reload when the choice was verifiably persisted;
 * otherwise apply in-memory (no reload loop).
 */
export function consentTransition(
  prev: ConsentChoice | null,
  next: ConsentChoice,
  gtmLoaded: boolean,
  persisted: boolean,
): "reload" | "apply" {
  if (gtmLoaded && persisted && optionalChanged(prev, next)) return "reload";
  return "apply";
}

/** First-party Google cookies this site can identify and expire itself. */
export function optionalCookieNames(cookieHeader: string, choice: ConsentChoice): string[] {
  const names = cookieHeader
    .split(";")
    .map((c) => c.split("=")[0]?.trim())
    .filter((n): n is string => !!n);
  return names.filter((n) => {
    const isAnalytics = /^_ga($|_)|^_gid$|^_gat/.test(n);
    const isAds = /^_gcl_|^_gac_/.test(n);
    return (isAnalytics && !choice.analytics) || (isAds && !choice.ads);
  });
}

/**
 * Inline <head> script. Runs before any tag: sets Consent Mode v2 defaults to denied,
 * then replays a stored valid choice as an update. It never loads any external script.
 */
export const consentBootstrapScript = `(function(){
if(window.__seoevoConsentInit)return;
window[${JSON.stringify(GA4_DISABLE_KEY)}]=true;
window.__seoevoConsentInit=true;
window.dataLayer=window.dataLayer||[];
window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};
gtag('consent','default',${JSON.stringify({ ...CONSENT_DEFAULTS, wait_for_update: 500 })});
gtag('set','ads_data_redaction',true);
gtag('set','url_passthrough',false);
try{
var raw=window.localStorage.getItem(${JSON.stringify(CONSENT_STORAGE_KEY)});
if(!raw)return;
var d=JSON.parse(raw),now=Date.now();
if(!d||d.v!==${CONSENT_VERSION}||typeof d.analytics!=='boolean'||typeof d.ads!=='boolean'||typeof d.ts!=='number'||!Number.isFinite(d.ts)||d.ts>now+60000||now-d.ts>${CONSENT_MAX_AGE_MS})return;
window[${JSON.stringify(GA4_DISABLE_KEY)}]=d.analytics!==true;
var a=d.ads?'granted':'denied',an=d.analytics?'granted':'denied';
window.__seoevoConsentApplied=an+'|'+a;
gtag('consent','update',{ad_storage:a,ad_user_data:a,ad_personalization:a,analytics_storage:d.analytics?'granted':'denied'});
window.dataLayer.push({event:${JSON.stringify(CONSENT_UPDATE_EVENT)},consent_analytics:d.analytics?'granted':'denied',consent_ads:a});
}catch(e){}
})();`;
